<script lang="ts">
  import type { GatFormFieldProps } from '../gat-form-field/gat-form-field.vue'

  export type GatComboboxModelValue = string | undefined

  export type GatComboboxItem<T = undefined> = T extends undefined
    ? {
        title: string
        value: string
        searchValue?: string
        dataTestid?: (item: GatComboboxItem<T>) => string
      }
    : {
        title: string
        value: string
        searchValue?: string
        metaData: T
      }

  export interface GatComboboxProps<T = undefined>
    extends GatFormFieldProps<GatComboboxModelValue> {
    modelValue: GatComboboxModelValue
    items: GatComboboxItem<T>[]
    placeholder?: string
    showSearch?: boolean
    popoverProps?: {
      align?: 'start' | 'center' | 'end'
      placeholderSearch?: string
      noItemText?: string
      class?: string
      width?: number
    }
  }

  export type GatComboboxEmits = {
    (e: 'update:modelValue', val: GatComboboxModelValue): void
  }

  export type GatComboboxSlots<T = undefined> = {
    'append-content': void
    'append-content-item': void
    'prepend-content-item': void
    'prepend-inner': void
    trigger: void
    default: void
    item(props: { item: GatComboboxItem<T>; selected: boolean }): void
    content: void
  }
</script>

<script setup lang="ts" generic="T = undefined">
  import { icons } from '@gat-ship/icons'
  import { computed, nextTick, onMounted, ref, useTemplateRef } from 'vue'
  import { cn } from '../../lib/utils'
  import { GatButton } from '../gat-button'
  import { GatIcon } from '../gat-icon'
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
  } from '../ui/command'
  import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
  import GatFormField from '../gat-form-field/gat-form-field.vue'
  import { testIds } from '@gat-ship/types'
  import { useGatForm } from '../gat-form/gat-form.vue'

  defineOptions({ inheritAttrs: false })

  defineSlots<GatComboboxSlots<T>>()

  const props = withDefaults(defineProps<GatComboboxProps<T>>(), {
    showOptional: true,
    showSearch: true,
    fieldType: undefined
  })

  const emit = defineEmits<GatComboboxEmits>()

  const comboboxValue = computed({
    get: () => props.modelValue,
    set: (value) => {
      emit('update:modelValue', value)
    }
  })

  function isSelected(value: GatComboboxItem['value']) {
    if (!value || !comboboxValue.value) {
      return false
    }
    return comboboxValue.value === value
  }

  const mouseIsOverButton = ref(false)

  const showClearButton = computed((): boolean => {
    if (props.required || !comboboxValue.value) {
      return false
    }
    if (!mouseIsOverButton.value) {
      return false
    }
    return true
  })

  const searchPlaceholder = computed((): string => {
    if (props.loading) {
      return 'Loading...'
    }
    return props.popoverProps?.placeholderSearch ?? 'Search'
  })

  const popoverContentWidth = ref<number>(250)

  const btn = useTemplateRef('combobox-button')
  const insetTriggerRef = useTemplateRef<HTMLElement>('inset-trigger')

  function focusTrigger() {
    const triggerEl =
      fieldType.value === 'default'
        ? (btn.value?.$el as HTMLElement | undefined)
        : insetTriggerRef.value
    if (triggerEl?.focus) {
      triggerEl.focus()
    }
  }

  function selectItem(value: GatComboboxItem['value']) {
    openValue.value = false
    comboboxValue.value = value
    nextTick(focusTrigger)
  }

  onMounted(() => {
    const popoverBtnWidth = btn.value?.$el.clientWidth ?? 250
    popoverContentWidth.value = popoverBtnWidth + 2 // +2 to account for borders.
  })

  const openValue = defineModel<boolean>('open', { default: false })

  const formContext = useGatForm()

  const fieldType = computed(() => {
    return props.fieldType ?? formContext.fieldType ?? 'default'
  })
  // const value = ref('')

  function handleValueChange(value: GatComboboxModelValue) {
    nextTick(() => {
      emit('update:modelValue', value)
    })
  }
</script>

<template>
  <Popover v-model:open="openValue">
    <gat-form-field
      v-if="fieldType === 'default'"
      v-bind="props"
      :value="comboboxValue"
      @value-updated="handleValueChange"
    >
      <template #prepend-inner v-if="$slots['prepend-inner']">
        <slot name="prepend-inner" />
      </template>
      <template
        #default="{ errorMessages, isValid, name, disabled, inputClass }"
      >
        <PopoverTrigger
          as-child
          @mouseenter="[(mouseIsOverButton = true)]"
          @mouseleave="[(mouseIsOverButton = false)]"
        >
          <slot
            name="trigger"
            v-bind="{
              selectedTitle: items?.find((item) => item.value === comboboxValue)
                ?.title
            }"
          >
            <gat-button
              ref="combobox-button"
              variant="outline"
              role="combobox"
              :data-testid="props.dataTestid"
              :aria-expanded="open"
              :class="cn(inputClass, 'overflow-hidden text-sm font-normal', {})"
              :disabled="disabled"
            >
              <span
                class="text-sm font-normal opacity-50 transition duration-200 ease-out group-hover/input:opacity-100 dark:text-neutral-400"
                v-if="!comboboxValue && placeholder"
                >{{ placeholder }}</span
              >
              <slot v-else>
                <div class="truncate">
                  {{
                    items?.find((item) => item.value === comboboxValue)?.title
                  }}
                </div>
              </slot>
              <div class="ml-auto flex items-center gap-1">
                <gat-button
                  v-if="showClearButton"
                  :loading="false"
                  variant="ghost"
                  size="icon"
                  :icon="icons.common.close"
                  class="text-foreground hover:bg-foreground/25"
                  @click.stop="comboboxValue = undefined"
                >
                </gat-button>
                <gat-icon
                  :icon="icons.common.collapsed"
                  class="ml-auto h-4 w-4 shrink-0 opacity-50"
                />
              </div>
            </gat-button>
          </slot>
        </PopoverTrigger>
        <PopoverContent
          class="bg-background p-0"
          :align="popoverProps?.align ?? 'start'"
          :class="popoverProps?.class"
          :style="{
            width: '100%',
            maxWidth: `${popoverProps?.width ?? popoverContentWidth}px`
          }"
        >
          <slot name="content">
            <Command class="w-full" :highlight-on-hover="true">
              <CommandInput
                v-if="items.length > 6 && showSearch"
                class="h-9"
                :placeholder="searchPlaceholder"
                :disabled="loading"
                :loading
              />
              <CommandList>
                <CommandEmpty v-if="loading">Loading</CommandEmpty>
                <CommandEmpty v-else>{{
                  popoverProps?.noItemText ?? 'No item found.'
                }}</CommandEmpty>
                <CommandGroup>
                  <slot name="prepend-content-item"></slot>
                  <CommandItem
                    v-for="item in items"
                    :key="item.value"
                    :value="item.value + (item.searchValue ?? '') + item.title"
                    :disabled="loading"
                    :data-testid="testIds.common.combobox.item(item.value)"
                    @select="selectItem(item.value)"
                    :class="[
                      {
                        'bg-primary/10 ring-primary/20 ring-1': isSelected(
                          item.value
                        )
                      }
                    ]"
                  >
                    <div
                      class="flex w-full min-w-0 items-center overflow-hidden"
                      :class="[{ 'font-medium': isSelected(item.value) }]"
                    >
                      <slot
                        name="item"
                        v-bind="{ item, selected: isSelected(item.value) }"
                      >
                        <div class="grow-1 flex">
                          {{ item.title }}
                        </div>
                      </slot>
                      <gat-icon
                        v-if="!$slots['item']"
                        :icon="icons.common.check"
                        :class="[
                          'ml-auto h-4 w-4',
                          comboboxValue === item.value
                            ? 'opacity-100'
                            : 'opacity-0'
                        ]"
                      />
                    </div>
                  </CommandItem>
                  <slot name="append-content-item"></slot>
                </CommandGroup>
              </CommandList>
              <slot name="append-content"></slot>
            </Command>
          </slot>
        </PopoverContent>
      </template>
    </gat-form-field>
    <template v-else>
      <PopoverTrigger
        as-child
        @mouseenter="[(mouseIsOverButton = true)]"
        @mouseleave="[(mouseIsOverButton = false)]"
      >
        <gat-form-field
          v-bind="props"
          :value="comboboxValue"
          validate-on="input"
          @click.stop.prevent
          @value-updated="handleValueChange"
        >
          <template
            #default="{ errorMessages, isValid, name, disabled, inputClass }"
          >
            <div
              ref="inset-trigger"
              :class="
                cn('cursor-pointer', inputClass, { 'pt-2': !props.label })
              "
              :id="name"
              role="combobox"
              :aria-expanded="openValue"
              :data-testid="props.dataTestid"
              tabindex="0"
              @keydown.space.enter.prevent="openValue = !openValue"
            >
              <div class="flex w-full flex-row items-center gap-2">
                <div
                  v-if="comboboxValue"
                  class="text-sm font-medium text-neutral-950 dark:text-neutral-200"
                >
                  <slot>
                    {{
                      items?.find((item) => item.value === comboboxValue)?.title
                    }}
                  </slot>
                </div>
                <div v-else-if="placeholder" class="text-sm text-neutral-400">
                  {{ placeholder }}
                </div>
                <div class="ml-auto shrink-0 items-center justify-center">
                  <gat-icon
                    :icon="icons.common.collapsed"
                    class="size-4 opacity-50"
                  />
                </div>
              </div>
            </div>
          </template>
        </gat-form-field>
      </PopoverTrigger>
      <PopoverContent
        class="bg-background p-0"
        :align="popoverProps?.align ?? 'start'"
        :class="popoverProps?.class"
        :style="{ width: `${popoverProps?.width ?? popoverContentWidth}px` }"
        @escape-key-down="focusTrigger()"
      >
        <Command class="w-full">
          <CommandInput
            v-if="items.length > 6 && showSearch"
            class="h-9"
            :placeholder="searchPlaceholder"
            :disabled="loading"
            :loading
          />
          <CommandList>
            <CommandEmpty v-if="loading">Loading</CommandEmpty>
            <CommandEmpty v-else>{{
              popoverProps?.noItemText ?? 'No item found.'
            }}</CommandEmpty>
            <CommandGroup>
              <CommandItem
                v-for="item in items"
                :key="item.value"
                :value="item.value + (item.searchValue ?? '') + item.title"
                :disabled="loading"
                :data-testid="testIds.common.combobox.item(item.value)"
                @select="selectItem(item.value)"
              >
                <div
                  class="flex grow items-center"
                  :class="[{ 'font-medium': isSelected(item.value) }]"
                >
                  <slot
                    name="item"
                    v-bind="{ item, selected: isSelected(item.value) }"
                  >
                    <div class="grow-1 flex">
                      {{ item.title }}
                    </div>
                  </slot>
                  <gat-icon
                    v-if="!$slots['item']"
                    :icon="icons.common.check"
                    :class="[
                      'ml-auto h-4 w-4',
                      comboboxValue === item.value ? 'opacity-100' : 'opacity-0'
                    ]"
                  />
                </div>
              </CommandItem>
              <slot name="append-content-item"></slot>
            </CommandGroup>
          </CommandList>
          <slot name="append-content"></slot>
        </Command>
      </PopoverContent>
    </template>
  </Popover>
</template>
